// Tally-style keyboard entry across the admin panel:
//   Enter / Shift+Enter  next / previous field (Enter on a form's last field saves it); Ctrl+Enter saves the form from anywhere
//   Up / Down            previous / next field; in an item grid, the same column of the row above / below
//   Left / Right         previous / next field once the cursor is at the start / end of the text
//   Esc                  closes the popup on top
// Dropdowns keep their own arrows; a handler that already dealt with a key (preventDefault) is left alone.
// Scrolling the mouse wheel over a focused number box no longer changes its value.

const FIELD_SELECTOR = [
  "input:not([type=hidden]):not([type=button]):not([type=submit]):not([type=file]):not([disabled]):not([tabindex='-1'])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "button[data-nav-field]:not([disabled])",
].join(", ");

const TEXT_TYPES = new Set(["text", "search", "email", "tel", "url", "password", "number", ""]);

// a field in an inert part of the form (a saved invoice line not opened for editing) cannot take the focus
const isVisible = (el) => !el.closest("[inert]") && (el.offsetParent !== null || el.getClientRects().length > 0);

/** The fields of the popup on top (or the page), in the order they are on screen. */
const fieldsAround = (el) => {
  const scope = el.closest(".modal-box") || document.querySelector(".modal-overlay:last-of-type .modal-box") || document.body;
  return Array.from(scope.querySelectorAll(FIELD_SELECTOR)).filter((field) => isVisible(field) && !field.readOnly);
};

const focusField = (field) => {
  field.focus();
  if (field.tagName === "INPUT" && TEXT_TYPES.has(field.type) && typeof field.select === "function") {
    try {
      field.select();
    } catch {
      // number inputs in some browsers do not support select()
    }
  }
};

/** Moves to the next (step 1) or previous (step -1) field; false when there is none. */
export const focusNext = (el, step = 1) => {
  const fields = fieldsAround(el);
  const at = fields.indexOf(el);
  const next = fields[at + step];
  if (!next) return false;
  focusField(next);
  return true;
};

/** In an item grid: the field in the same column of the row above (-1) / below (1). */
const focusSameColumn = (el, step) => {
  const cell = el.closest("td");
  const row = cell?.parentElement;
  if (!cell || !row) return false;
  const column = Array.from(row.children).indexOf(cell);
  let target = step > 0 ? row.nextElementSibling : row.previousElementSibling;
  while (target) {
    const field = target.children[column]?.querySelector(FIELD_SELECTOR);
    if (field && isVisible(field)) {
      focusField(field);
      return true;
    }
    target = step > 0 ? target.nextElementSibling : target.previousElementSibling;
  }
  return false;
};

const lastFieldOfForm = (el) => {
  if (!el.form) return false;
  const fields = Array.from(el.form.querySelectorAll(FIELD_SELECTOR)).filter(isVisible);
  return fields[fields.length - 1] === el;
};

const caretAt = (el, end) => {
  try {
    if (el.selectionStart === null || el.selectionStart === undefined) return !el.value;
    if (el.selectionStart !== el.selectionEnd) return false;
    return end ? el.selectionEnd === el.value.length : el.selectionStart === 0;
  } catch {
    return !el.value;
  }
};

const closeTopPopup = () => {
  const overlays = document.querySelectorAll(".modal-overlay");
  const top = overlays[overlays.length - 1];
  const close = top?.querySelector(".close-btn");
  if (close) {
    close.click();
    return true;
  }
  return false;
};

const onKeyDown = (e) => {
  if (e.defaultPrevented || e.isComposing || e.altKey || e.metaKey) return;
  const el = e.target;
  if (e.key === "Escape") {
    if (closeTopPopup()) e.preventDefault();
    return;
  }
  if (!(el instanceof HTMLElement) || !el.matches(FIELD_SELECTOR)) return;
  const tag = el.tagName;
  const type = (el.getAttribute("type") || "").toLowerCase();

  if (e.key === "Enter") {
    if (tag === "TEXTAREA" && !e.ctrlKey) return;
    if (tag === "BUTTON") return; // a dropdown trigger opens itself
    if (e.ctrlKey) {
      if (el.form) {
        e.preventDefault();
        el.form.requestSubmit();
      }
      return;
    }
    e.preventDefault();
    if (!e.shiftKey && lastFieldOfForm(el)) {
      el.form.requestSubmit();
      return;
    }
    focusNext(el, e.shiftKey ? -1 : 1);
    return;
  }

  if (tag === "SELECT" || tag === "TEXTAREA" || type === "date" || type === "datetime-local" || type === "time") return;
  if (tag === "BUTTON") {
    if (e.key === "ArrowUp" || e.key === "ArrowDown") return; // opens the dropdown
  }

  if (e.key === "ArrowDown" || e.key === "ArrowUp") {
    const step = e.key === "ArrowDown" ? 1 : -1;
    e.preventDefault(); // also stops number boxes stepping their value
    if (el.closest(".items-table") && focusSameColumn(el, step)) return;
    focusNext(el, step);
    return;
  }
  if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
    if (tag === "BUTTON" || type === "checkbox" || type === "radio" || caretAt(el, e.key === "ArrowRight")) {
      if (focusNext(el, e.key === "ArrowRight" ? 1 : -1)) e.preventDefault();
    }
  }
};

/** A wheel scroll over a focused number box would step its value: the box loses focus instead and the page scrolls. */
const onWheel = (e) => {
  const el = document.activeElement;
  if (el && el.tagName === "INPUT" && el.type === "number" && (e.target === el || el.contains(e.target))) el.blur();
};

let installed = false;

/** Installs the keyboard and wheel handling once for the whole app. */
export const installKeyboardNav = () => {
  if (installed) return () => {};
  installed = true;
  document.addEventListener("keydown", onKeyDown);
  document.addEventListener("wheel", onWheel, { passive: true });
  return () => {
    installed = false;
    document.removeEventListener("keydown", onKeyDown);
    document.removeEventListener("wheel", onWheel);
  };
};
